import React, { useState } from 'react';
import { ArrowDown, ChevronRight } from 'lucide-react';

const LayerBox = ({ title, children, bgColor, onShowCode, code }) => (
  <div className={`${bgColor} p-4 rounded-lg shadow-lg border border-gray-200 w-full`}>
    <div className="flex justify-between items-center mb-3">
      <h3 className="font-bold text-lg">{title}</h3>
      {code && (
        <button 
          onClick={onShowCode}
          className="px-3 py-1 bg-white rounded-md shadow hover:bg-gray-50"
        >
          Voir le code
        </button>
      )}
    </div>
    {children}
  </div>
);

const StateBox = ({ title, children }) => (
  <div className="bg-white/80 p-3 rounded-md shadow-sm mb-2 border border-gray-200">
    <h4 className="font-semibold mb-2">{title}</h4>
    {children}
  </div>
);

const Arrow = () => (
  <div className="flex justify-center my-2">
    <ArrowDown className="text-gray-400" size={24} />
  </div>
);

const CodeModal = ({ code, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4">
    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-auto">
      <div className="flex justify-between items-center p-4 border-b">
        <h3 className="font-bold">Implémentation</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
      </div>
      <pre className="p-4 bg-gray-50 text-sm overflow-auto">
        <code>{code}</code>
      </pre>
    </div>
  </div>
);

export default function WorkflowImplementation() {
  const [selectedCode, setSelectedCode] = useState(null);

  const workflowStates = [
    "RECEIVED_MT103",
    "MT_PARSED",
    "MT_VALIDATED",
    "MX_CONVERTED",
    "MX_ENRICHED",
    "MX_VALIDATED",
    "COMPLETED"
  ];

  const implementations = {
    domain: `public class MessageConversionWorkflowDefinition {
    public static final Set<WorkflowState> STATES = Set.of(
        new WorkflowState("RECEIVED_MT103", true),    // État initial - MT reçu
        new WorkflowState("MT_PARSED", false),        // MT analysé
        new WorkflowState("MT_VALIDATED", false),     // MT validé
        new WorkflowState("MX_CONVERTED", false),     // Converti en MX
        new WorkflowState("MX_ENRICHED", false),      // MX enrichi
        new WorkflowState("MX_VALIDATED", false),     // MX validé
        new WorkflowState("COMPLETED", true),         // Conversion réussie
        new WorkflowState("REJECTED", true)           // Conversion échouée
    );
}`,
    application: `@Service
@Transactional
public class MT103ConversionWorkflowService implements ConvertMT103ToPacs008UseCase {
    @Override
    public ConversionResult convertMessage(ConvertMT103Command command) {
        UUID conversionId = UUID.randomUUID();
        workflowPort.updateWorkflowState(conversionId, "RECEIVED_MT103");

        try {
            // 1. Charger et parser le MT103
            MT103Message mt103 = mt103Repository.findById(command.getMt103Id())
                .orElseThrow(() -> new MessageNotFoundException(command.getMt103Id()));
            workflowPort.updateWorkflowState(conversionId, "MT_PARSED");

            // ... reste de l'implémentation
        }
    }
}`,
    infrastructure: `@Configuration
public class MessageConversionWorkflowConfig {
    @Bean
    public StateMachineFactory<String, String> conversionStateMachineFactory() {
        StateMachineConfigurationBuilder<String, String> config = 
            new StateMachineConfigurationBuilder<>();

        config.withStates()
            .initial("RECEIVED_MT103")
            .state("MT_PARSED")
            .state("MT_VALIDATED")
            .state("MX_CONVERTED")
            .state("MX_ENRICHED")
            .state("MX_VALIDATED")
            .end("COMPLETED")
            .end("REJECTED");

        // ... configuration des transitions
        return new StateMachineFactory<>(config);
    }
}`,
    interface: `@RestController
@RequestMapping("/api/conversions")
public class MessageConversionController {
    private final ConvertMT103ToPacs008UseCase conversionUseCase;

    @PostMapping("/mt103-to-pacs008")
    public ResponseEntity<ConversionResultDTO> convertMT103ToPacs008(
        @Valid @RequestBody MT103ConversionRequest request
    ) {
        ConvertMT103Command command = new ConvertMT103Command(
            request.getMt103Id(),
            request.getConversionParameters()
        );
        
        ConversionResult result = conversionUseCase.convertMessage(command);
        return ResponseEntity.ok(ConversionResultDTO.from(result));
    }
}`
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <h1 className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-8">
          MT103 to Pacs.008 Conversion Implementation
        </h1>

        <div className="space-y-4">
          <LayerBox 
            title="Interface Layer" 
            bgColor="bg-blue-100"
            code={implementations.interface}
            onShowCode={() => setSelectedCode('interface')}
          >
            <StateBox title="MessageConversionController">
              <div className="space-y-2 text-sm">
                <div>• /api/conversions/mt103-to-pacs008 (POST)</div>
                <div>• /api/conversions/{"{conversionId}"}/status (GET)</div>
              </div>
            </StateBox>
          </LayerBox>

          <Arrow />

          <LayerBox 
            title="Application Layer" 
            bgColor="bg-green-100"
            code={implementations.application}
            onShowCode={() => setSelectedCode('application')}
          >
            <StateBox title="MT103ConversionWorkflowService">
              <div className="space-y-2 text-sm">
                {workflowStates.map((state, index) => (
                  <div key={state} className="flex items-center gap-2">
                    {index > 0 && <ChevronRight size={12} className="text-gray-400" />}
                    <span>{state}</span>
                  </div>
                ))}
              </div>
            </StateBox>
          </LayerBox>

          <Arrow />

          <LayerBox 
            title="Domain Layer" 
            bgColor="bg-yellow-100"
            code={implementations.domain}
            onShowCode={() => setSelectedCode('domain')}
          >
            <div className="grid grid-cols-2 gap-4">
              <StateBox title="Services">
                <div className="space-y-2 text-sm">
                  <div>• MT103ValidationDomainService</div>
                  <div>• MTMXTransformationDomainService</div>
                </div>
              </StateBox>
              <StateBox title="Models">
                <div className="space-y-2 text-sm">
                  <div>• MT103Message</div>
                  <div>• Pacs008Swift</div>
                  <div>• ValidationResult</div>
                </div>
              </StateBox>
            </div>
          </LayerBox>

          <Arrow />

          <LayerBox 
            title="Infrastructure Layer" 
            bgColor="bg-red-100"
            code={implementations.infrastructure}
            onShowCode={() => setSelectedCode('infrastructure')}
          >
            <div className="grid grid-cols-2 gap-4">
              <StateBox title="Configuration">
                <div className="space-y-2 text-sm">
                  <div>• MessageConversionWorkflowConfig</div>
                  <div>• StateMachineFactory</div>
                </div>
              </StateBox>
              <StateBox title="Persistence">
                <div className="space-y-2 text-sm">
                  <div>• MessageConversionEntity</div>
                  <div>• ConversionStateHistory</div>
                  <div>• ConversionError</div>
                </div>
              </StateBox>
            </div>
          </LayerBox>
        </div>

        {selectedCode && (
          <CodeModal 
            code={implementations[selectedCode]}
            onClose={() => setSelectedCode(null)}
          />
        )}
      </div>
    </div>
  );
}